import { type MouseEvent, useState } from "react";
import { useNavigate } from "react-router";

import { t } from "i18next";

import { Menu as MenuIcon } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  useMediaQuery,
  useTheme
} from "@mui/material";

import { onPrimarySurfaceColor } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

import ThemeToggle from "@/src/components/atoms/ThemeToggle/ThemeToggle";
import UserInfo from "@/src/components/molecules/UserInfo";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";

import LanguageSwitcher from "../../../localizations/languageSwitcher";
import { notificationAtom } from "../../../state/notificationAtom";
import { BaanaLogo } from "../../atoms";
import {
  consumerHeaderLoginButtonSx,
  headerThemeToggleDividerSx
} from "./consumerHeader.styles";

import { useSetAtom } from "jotai";

const AdminHeader = () => {
  const navigate = useNavigate();
  const setNotification = useSetAtom(notificationAtom);
  const muiTheme = useTheme();
  const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up("md"));
  const { isAuthenticated, isAuthenticating, user, login, logout } = useAuth();
  const [adminMenuToggleButtonElement, setadminMenuToggleButtonElement] =
    useState<null | HTMLElement>(null);

  const open = Boolean(adminMenuToggleButtonElement);

  const openUserMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setadminMenuToggleButtonElement(event.currentTarget);
  };

  const closeUserMenu = () => {
    setadminMenuToggleButtonElement(null);
  };

  const handleNavigation = (url: string) => {
    navigate(url);
  };

  return (
    <>
      <AppBar position="sticky" id="adminHeader">
        <Toolbar
          sx={{
            justifyContent: "space-between"
          }}
        >
          <Box
            id="adminLogoButton"
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              pt: 1
            }}
            onClick={() => {
              handleNavigation("/admin");
            }}
          >
            <Box sx={{ width: "6rem" }}>
              <BaanaLogo color={onPrimarySurfaceColor(muiTheme)} />
            </Box>
          </Box>

          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            {isAuthenticated && !isAuthenticating && (
              <UserInfo
                firstName={user?.firstName}
                lastName={user?.lastName}
                roleName="Pääkäyttäjä"
              />
            )}

            {!isAuthenticated && (
              <Button
                id="adminMenuLoginButton"
                variant="contained"
                sx={schemeSx((theme) => ({
                  ...consumerHeaderLoginButtonSx(theme),
                  marginRight: 1
                }))}
                onClick={() => {
                  login("admin", LoginSource.ADMIN_HEADER);
                }}
              >
                {t("general.loginShort")}
              </Button>
            )}
            <LanguageSwitcher />

            <IconButton
              id="adminMenuToggleButton"
              size="large"
              edge="start"
              color="inherit"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={openUserMenu}
              sx={{ borderRadius: 2 }}
            >
              <MenuIcon />
            </IconButton>

            {isLargeScreen && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={headerThemeToggleDividerSx}
                />
                <ThemeToggle userType="admin" />
              </>
            )}

            {!isAuthenticating && (
              <Menu
                id="adminMenu"
                anchorEl={adminMenuToggleButtonElement}
                open={open}
                onClose={() => closeUserMenu()}
                sx={{ ".MuiPaper-root": { borderRadius: "6px" } }}
                slotProps={{
                  list: {
                    "aria-labelledby": "adminMenuToggleButton"
                  }
                }}
              >
                {isAuthenticated && (
                  <MenuItem
                    id="adminMenuLogoutMenuItem"
                    onClick={() => {
                      closeUserMenu();
                      setNotification(null);
                      logout();
                    }}
                  >
                    {t("general.logout")}
                  </MenuItem>
                )}

                {!isAuthenticated && (
                  <MenuItem
                    id="adminMenuLoginMenuItem"
                    onClick={() => {
                      closeUserMenu();
                      setNotification(null);
                      login("admin", LoginSource.ADMIN_MENU);
                    }}
                  >
                    {t("general.login")}
                  </MenuItem>
                )}
                {!isLargeScreen && (
                  <>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <ThemeToggle userType="admin" showLabel surface="menu" />
                    </Box>
                  </>
                )}
              </Menu>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default AdminHeader;
